import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Phone, ArrowRight, Eye, ArrowLeft, ShieldCheck, Mail, Lock, Sparkles, Check } from "lucide-react";
import { useGoogleLogin } from "@react-oauth/google";
import { useApp } from "@/context/AppContext";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import CreditNote from "@/components/CreditNote";
import { GOOGLE_NATIVE_ENABLED } from "@/lib/flags";

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const USE_NATIVE_GOOGLE = !!GOOGLE_CLIENT_ID && GOOGLE_NATIVE_ENABLED;

// Phone/SMS sign-in via Twilio Verify. Hidden until the Twilio account is upgraded out of
// trial mode (trial accounts can only SMS pre-verified numbers, so real users would error).
// Flip to true once Twilio billing is active.
const PHONE_SIGNIN_ENABLED = false;

const FREE_PERKS = [
  "Unlimited free posts, likes, comments & shares",
  "Post travel plans, rides, meals & events — always free",
  "1 free AI search",
  "1 free Save & Earn report",
  "1 free Product Safety product scan",
];

const TRANSMITTED = "Your mission code has been transmitted. Please check your inbox or WhatsApp to continue.";

export default function Auth() {
  const { setUser, user } = useApp();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const ref = params.get("ref") || (typeof localStorage !== "undefined" ? localStorage.getItem("mykind_ref") : "") || "";
  // Already signed in? Don't show the login screen — go to the app.
  useEffect(() => {
    if (user) nav("/", { replace: true });
  }, [user, nav]);
  useEffect(() => {
    if (ref) {
      api.post("/affiliate/click", { code: ref }).catch(() => {});
      try { localStorage.setItem("rud_ref", ref); } catch (e) { /* noop */ }
    }
  }, [ref]);

  const [showPhone, setShowPhone] = useState(false);
  const [pstep, setPstep] = useState("input");
  const [phone, setPhone] = useState("");
  const [pcode, setPcode] = useState("");
  const [pdemo, setPdemo] = useState("");
  const [pinfo, setPinfo] = useState("");
  const [pbusy, setPbusy] = useState(false);
  const [perr, setPerr] = useState("");

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Shared post-login: set user, welcome, honor any saved redirect.
  const afterLogin = (userData, isNew) => {
    setUser(userData);
    toast.success(isNew ? "Welcome to My Kind ✨" : "Welcome back");
    let dest = "/";
    try { const s = sessionStorage.getItem("rud_post_login_redirect"); if (s) { dest = s; sessionStorage.removeItem("rud_post_login_redirect"); } } catch (er) { /* noop */ }
    nav(dest);
  };

  const sendPhone = async (e) => {
    if (e) e.preventDefault();
    if (!phone.trim()) { setPerr("Enter your phone number with country code, e.g. +14155552671"); return; }
    setPbusy(true); setPerr(""); setPdemo(""); setPinfo("");
    try {
      const { data } = await api.post("/auth/phone/start", { phone: phone.trim() });
      setPstep("code");
      if (data.demo && data.dev_code) { setPdemo(data.dev_code); toast.success("Demo code generated below"); }
      else { setPinfo(data.message || "We've sent your code by SMS."); toast.success(TRANSMITTED); }
    } catch (e2) {
      setPerr(!e2.response ? "Couldn't reach the server. Try again." : apiError(e2.response?.data?.detail));
    } finally { setPbusy(false); }
  };

  const verifyPhone = async (e) => {
    e.preventDefault();
    if (!pcode.trim()) { setPerr("Enter the verification code."); return; }
    setPbusy(true); setPerr("");
    try {
      const { data } = await api.post("/auth/phone/verify", { phone: phone.trim(), code: pcode.trim(), referral_code: ref || null });
      setUser(data.user);
      toast.success(data.is_new ? "Welcome to My Kind ✨" : "Welcome back");
      let dest = "/";
      try { const s = sessionStorage.getItem("rud_post_login_redirect"); if (s) { dest = s; sessionStorage.removeItem("rud_post_login_redirect"); } } catch (er) { /* noop */ }
      nav(dest);
    } catch (e2) {
      setPerr(!e2.response ? "Couldn't reach the server. Try again." : apiError(e2.response?.data?.detail));
    } finally { setPbusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex relative grid-bg items-center justify-center p-12 overflow-hidden border-r border-rud">
        <div className="absolute top-1/3 left-1/4 w-96 h-96 brand-glow rounded-full pointer-events-none" />
        <div className="absolute bottom-10 right-10 w-72 h-72 brand-glow-2 rounded-full pointer-events-none" />
        <div className="relative max-w-md">
          <div className="w-12 h-12 grid place-items-center text-brand mb-6"><Eye size={40} strokeWidth={1.6} className="rotate-90" /></div>
          <h2 className="font-serif text-4xl tracking-tighter leading-[1.08]">
            One App for <span className="text-brand">everything</span>, for <span className="text-brand">everyone</span> — built for a <span className="text-brand">Decentralised Economy</span>.
          </h2>
          <p className="text-sec text-xs uppercase tracking-widest mt-3">Individual · Business · Org · Community · Government</p>
          <p className="text-sec mt-5 leading-relaxed">
            My Kind is the first-of-its-kind <span className="text-[var(--rud-text)] font-medium">6th-Gen app</span> — <span className="text-[var(--rud-text)] font-medium">self-evolving, autonomous & data-sovereign</span>. Leave behind the clutter, noise and subscriptions of old apps. <span className="text-[var(--rud-text)] font-medium">Own your data</span> and unlock <span className="text-brand font-medium">infinite learning & earning</span>.
          </p>
          <div className="mt-6 rounded-2xl border border-brand/25 bg-brand/5 p-5" data-testid="free-perks-promo">
            <div className="text-[11px] uppercase tracking-widest text-brand flex items-center gap-1.5"><Sparkles size={13} /> Free to start — no card needed</div>
            <p className="text-sec text-sm mt-2">Create your profile free. Every new member instantly gets:</p>
            <ul className="mt-3 space-y-2">
              {FREE_PERKS.map((p) => (
                <li key={p} className="flex items-center gap-2 text-sm"><Check size={15} className="text-brand shrink-0" /> {p}</li>
              ))}
            </ul>
            <p className="text-[11px] text-sec mt-3">Posting is always free — we only earn a 15% fee when you actually pay for a service (a ride, a hosted meal or an event).</p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
          <Link to="/" className="lg:hidden flex items-center gap-2 mb-8"><div className="w-8 h-8 grid place-items-center text-brand"><Eye size={26} strokeWidth={1.8} className="rotate-90" /></div><span className="font-serif text-2xl">My Kind</span></Link>

          <h1 className="font-serif text-3xl tracking-tight mb-2">Join My Kind</h1>
          <p className="text-sec text-sm mb-5">Sign in with Google to create your account. It's free, and takes a few seconds.</p>

          {USE_NATIVE_GOOGLE ? (
            <>
              <NativeGoogleButton referralCode={ref} onLogin={afterLogin} />
              <button type="button" data-testid="emergent-login-fallback" onClick={googleLogin} className="mt-3 w-full text-center text-[11px] text-sec hover:text-brand transition-colors">
                Having trouble? Use secure backup sign-in
              </button>
            </>
          ) : (
            <button data-testid="google-login" onClick={googleLogin} style={{ backgroundColor: "#e5e7eb", color: "#111827" }} className="rounded-full w-full h-12 flex items-center justify-center gap-2.5 text-sm font-medium hover:opacity-90 transition-opacity">
              <img src="https://www.svgrepo.com/show/475656/google-color.svg" alt="" className="w-5 h-5 bg-white rounded-full p-0.5" /> Continue with Google
            </button>
          )}

          {PHONE_SIGNIN_ENABLED && (!showPhone ? (
            <button type="button" data-testid="use-phone-link" onClick={() => setShowPhone(true)} className="mt-4 w-full text-center text-sm text-sec hover:text-brand transition-colors flex items-center justify-center gap-1.5">
              <Phone size={14} /> Use my phone number instead
            </button>
          ) : (
            <div className="mt-5 rounded-xl border border-rud bg-white/5 p-4" data-testid="phone-auth">
              <div className="flex items-center gap-3 my-1 text-xs text-sec"><div className="h-px bg-white/10 flex-1" /> phone sign-in <div className="h-px bg-white/10 flex-1" /></div>
              {pstep === "input" ? (
                <form onSubmit={sendPhone} className="space-y-3 mt-2" data-testid="phone-input-step">
                  <div className="relative">
                    <Phone size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sec" />
                    <input data-testid="phone-input" type="tel" required placeholder="+1 415 555 2671" value={phone} onChange={(e) => setPhone(e.target.value)} className="rud-input w-full rounded-lg pl-10 pr-4 py-3 text-sm" />
                  </div>
                  {perr && <p className="text-sm text-red-400" data-testid="phone-error">{perr}</p>}
                  <button data-testid="phone-send" disabled={pbusy} className="btn-primary w-full h-11 flex items-center justify-center gap-2 disabled:opacity-60">{pbusy ? <Loader2 size={18} className="animate-spin" /> : <>Send code <ArrowRight size={16} /></>}</button>
                  <button type="button" onClick={() => { setShowPhone(false); setPerr(""); }} className="text-[11px] text-sec hover:text-brand">Back to Google</button>
                </form>
              ) : (
                <form onSubmit={verifyPhone} className="space-y-3 mt-2" data-testid="phone-code-step">
                  <button type="button" data-testid="phone-back" onClick={() => { setPstep("input"); setPerr(""); setPcode(""); }} className="flex items-center gap-1.5 text-xs text-sec hover:text-[var(--rud-text)]"><ArrowLeft size={14} /> Change number</button>
                  <p className="text-sm text-sec">Enter the code sent to <b className="text-[var(--rud-text)]">{phone}</b>.</p>
                  {pinfo && !pdemo && <p className="text-xs text-sec bg-brand/5 border border-brand/20 rounded-lg p-2.5" data-testid="phone-info">{pinfo}</p>}
                  {pdemo && (
                    <div className="rounded-lg bg-brand/10 border border-brand/30 p-3 text-sm" data-testid="phone-demo-code">
                      <span className="text-xs uppercase tracking-widest text-brand">Demo code</span>
                      <div className="font-mono text-2xl tracking-widest text-[var(--rud-text)] mt-1">{pdemo}</div>
                    </div>
                  )}
                  <div className="relative">
                    <ShieldCheck size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sec" />
                    <input data-testid="phone-otp-input" inputMode="numeric" required placeholder="6-digit code" value={pcode} onChange={(e) => setPcode(e.target.value)} className="rud-input w-full rounded-lg pl-10 pr-4 py-3 text-sm tracking-widest" />
                  </div>
                  {perr && <p className="text-sm text-red-400" data-testid="phone-error">{perr}</p>}
                  <button data-testid="phone-verify" disabled={pbusy} className="btn-primary w-full h-11 flex items-center justify-center gap-2 disabled:opacity-60">{pbusy ? <Loader2 size={18} className="animate-spin" /> : <>Verify &amp; continue <ArrowRight size={16} /></>}</button>
                  <button type="button" data-testid="phone-resend" onClick={() => sendPhone()} className="text-xs text-brand hover:underline">Resend code</button>
                </form>
              )}
            </div>
          ))}

          <div className="mt-5 rounded-xl border border-brand/20 bg-brand/5 p-3" data-testid="approval-note">
            <p className="text-[12px] text-sec leading-relaxed"><b className="text-brand">Welcome — everyone's family here. 🌍</b> You can sign up and explore right away. A few actions unlock once our team gives your account a quick welcome-approval (usually within a day).</p>
          </div>

          {ref && <p className="text-xs text-brand mt-3">Referred by code {ref} 🎉 — you'll get bonus credits.</p>}

          <div className="mt-4"><CreditNote compact /></div>

          <p className="text-[11px] text-sec mt-6">Your details are used only to secure your account. Never shown on your public profile or to other users.</p>
        </motion.div>
      </div>
    </div>
  );
}

function NativeGoogleButton({ referralCode, onLogin }) {
  const [busy, setBusy] = useState(false);
  const login = useGoogleLogin({
    flow: "auth-code",
    onSuccess: async (resp) => {
      setBusy(true);
      try {
        const { data } = await api.post("/auth/google/native", { code: resp.code, referral_code: referralCode || null });
        onLogin(data.user, true);
      } catch (e2) {
        if (e2.response?.status === 403 && e2.response?.data?.detail === "invite_only") {
          toast.error("You're on the waitlist — we'll email you once a spot opens. 🌍");
        } else {
          toast.error(!e2.response ? "Couldn't reach the server. Try again." : apiError(e2.response?.data?.detail));
        }
      } finally { setBusy(false); }
    },
    onError: () => toast.error("Google sign-in failed. Please try again."),
  });
  return (
    <button
      data-testid="google-login"
      onClick={() => login()}
      disabled={busy}
      style={{ backgroundColor: "#e5e7eb", color: "#111827" }}
      className="rounded-full w-full h-12 flex items-center justify-center gap-2.5 text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-60"
    >
      {busy ? <Loader2 size={18} className="animate-spin" /> : (
        <><img src="https://www.svgrepo.com/show/475656/google-color.svg" alt="" className="w-5 h-5 bg-white rounded-full p-0.5" /> Continue with Google</>
      )}
    </button>
  );
}

function AdminLogin({ login, adminKey, onDone, onCancel }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [forgot, setForgot] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try { await login(email, password, adminKey); toast.success("Welcome back"); onDone(); }
    catch (e2) { setErr(apiError(e2.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  if (forgot) return <ForgotPassword onCancel={() => setForgot(false)} />;
  return (
    <form onSubmit={submit} className="space-y-2" data-testid="admin-login-form">
      <div className="text-xs uppercase tracking-widest text-sec flex items-center gap-1.5"><Lock size={12} /> Password sign-in</div>
      <input data-testid="admin-email" type="email" required placeholder="you@…" value={email} onChange={(e) => setEmail(e.target.value)} className="rud-input w-full rounded-lg px-3 py-2.5 text-sm" />
      <input data-testid="admin-password" type="password" required placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className="rud-input w-full rounded-lg px-3 py-2.5 text-sm" />
      {err && <p className="text-xs text-red-400" data-testid="admin-error">{err}</p>}
      <div className="flex gap-2">
        <button data-testid="admin-submit" disabled={busy} className="btn-primary rounded-lg px-4 py-2 text-sm flex-1 flex items-center justify-center gap-1.5 disabled:opacity-60">
          {busy ? <Loader2 size={15} className="animate-spin" /> : "Sign in"}
        </button>
        <button type="button" onClick={onCancel} className="text-xs text-sec hover:text-[var(--rud-text)] px-2">Cancel</button>
      </div>
      <button type="button" data-testid="forgot-password-link" onClick={() => setForgot(true)} className="text-[11px] text-brand hover:underline">Forgot password? Send a recovery code</button>
    </form>
  );
}

function ForgotPassword({ onCancel }) {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [pwd, setPwd] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const sendCode = async (e) => {
    e.preventDefault(); setBusy(true); setMsg("");
    try {
      const { data } = await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setMsg(data.demo ? `Demo code: ${data.code}` : "We've emailed a recovery code to your registered email.");
      setStep(2);
    } catch (e2) { toast.error(apiError(e2.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const reset = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/auth/reset-password", { email: email.trim().toLowerCase(), code: code.trim(), new_password: pwd });
      toast.success("Password updated — please sign in.");
      onCancel();
    } catch (e2) { toast.error(apiError(e2.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-2" data-testid="forgot-password-form">
      <div className="text-xs uppercase tracking-widest text-sec flex items-center gap-1.5"><Lock size={12} /> Account recovery</div>
      {step === 1 ? (
        <form onSubmit={sendCode} className="space-y-2">
          <p className="text-[11px] text-sec">Enter the email you registered with — we'll send a one-time recovery code.</p>
          <input data-testid="forgot-email" type="email" required placeholder="you@…" value={email} onChange={(e) => setEmail(e.target.value)} className="rud-input w-full rounded-lg px-3 py-2.5 text-sm" />
          <div className="flex gap-2">
            <button data-testid="forgot-send" disabled={busy} className="btn-primary rounded-lg px-4 py-2 text-sm flex-1 flex items-center justify-center gap-1.5 disabled:opacity-60">{busy ? <Loader2 size={15} className="animate-spin" /> : "Send recovery code"}</button>
            <button type="button" onClick={onCancel} className="text-xs text-sec px-2">Back</button>
          </div>
        </form>
      ) : (
        <form onSubmit={reset} className="space-y-2">
          {msg && <p className="text-[11px] text-brand">{msg}</p>}
          <input data-testid="forgot-code" required placeholder="Recovery code" value={code} onChange={(e) => setCode(e.target.value)} className="rud-input w-full rounded-lg px-3 py-2.5 text-sm" />
          <input data-testid="forgot-newpwd" type="password" required placeholder="New password" value={pwd} onChange={(e) => setPwd(e.target.value)} className="rud-input w-full rounded-lg px-3 py-2.5 text-sm" />
          <div className="flex gap-2">
            <button data-testid="forgot-reset" disabled={busy} className="btn-primary rounded-lg px-4 py-2 text-sm flex-1 flex items-center justify-center gap-1.5 disabled:opacity-60">{busy ? <Loader2 size={15} className="animate-spin" /> : "Reset password"}</button>
            <button type="button" onClick={onCancel} className="text-xs text-sec px-2">Cancel</button>
          </div>
        </form>
      )}
    </div>
  );
}


export function AdminLoginPage() {
  const { login } = useApp();
  const nav = useNavigate();
  const { key } = useParams();
  // For the clean root-level secret URL the key IS the path; fall back to the pathname.
  const portalKey = key || decodeURIComponent((typeof window !== "undefined" ? window.location.pathname : "").replace(/^\//, ""));
  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-sm rud-card rounded-2xl p-6" data-testid="admin-login-page">
        <div className="flex items-center gap-2 mb-1"><ShieldCheck size={18} className="text-brand" /><h1 className="text-lg font-semibold">Admin sign-in</h1></div>
        <p className="text-[11px] text-sec mb-4">Staff & administrators only.</p>
        <AdminLogin login={login} adminKey={portalKey} onDone={() => nav("/admin/access")} onCancel={() => nav("/")} />
      </motion.div>
    </div>
  );
}
